/**
 * Market List Item Component
 * 
 * Individual market preview card for the market list.
 * Fixed height card with full-height image on left.
 * Layout: Stats at top, question in middle, options at bottom.
 */

import React from 'react';
import { EnrichedMarket } from '../../contexts/MarketProvider';
import { marketApiService } from '../../services/marketApiService';
import { LAMPORTS_PER_SOL } from '@solana/web3.js';
import { displayQuestion, displayOptionTitleCase } from '../../utils/displayText';

// Default image when market has no image
const DEFAULT_MARKET_IMAGE = '/assets/icons/imo-evergreen-floating.png';

interface MarketListItemProps {
  market: EnrichedMarket;
  onClick: () => void;
}

export const MarketListItem: React.FC<MarketListItemProps> = ({ market, onClick }) => {
  const status = marketApiService.getMarketStatus(market);
  const timeDisplay = marketApiService.getTimeDisplay(market);
  const isResolved = status === 'closed' && !!market.outcome;
  
  // Get market image or use default
  const marketImage = market.image_url || DEFAULT_MARKET_IMAGE;
  const isDefaultImage = !market.image_url;
  
  // Format SOL amount
  const formatSol = (lamports: number): string => {
    const sol = lamports / LAMPORTS_PER_SOL;
    if (sol >= 1000) {
      return `${(sol / 1000).toFixed(1)}k`;
    }
    return sol.toFixed(2);
  };
  
  // Get pool size from on-chain state or show placeholder
  const poolSize = market.onChainState 
    ? formatSol(market.onChainState.totalDeposits) 
    : '—';
  
  // Get winning option info for resolved markets
  const winnerInfo = isResolved && market.outcome ? {
    name: market.outcome.winning_option_name,
    index: market.outcome.winning_option_index
  } : null;
  
  // Get all options sorted by percentage (highest first), max 4
  const getAllOptions = () => {
    if (!market.onChainState?.options) {
      // No on-chain data, show all options with 0%
      return market.options.slice(0, 4).map((label, index) => ({
        label,
        percentage: 0,
        index,
        isWinner: false
      }));
    }
    
    // Sort options by percentage descending
    const sortedOptions = [...market.onChainState.options]
      .sort((a, b) => b.percentage - a.percentage)
      .slice(0, 4);
    
    return sortedOptions.map(opt => ({
      label: market.options[opt.optionIndex],
      percentage: opt.percentage,
      index: opt.optionIndex,
      isWinner: isResolved && winnerInfo?.index === opt.optionIndex
    }));
  };
  
  const allOptions = getAllOptions();
  
  // Format how long ago the market ended
  const getEndedAgo = (): string => {
    const endTime = new Date(market.ends_at).getTime();
    const now = Date.now();
    const diffMs = now - endTime;
    
    if (diffMs < 0) return '';
    
    const minutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just ended';
  };
  
  return (
    <div className={`market-list-item market-${status} ${isResolved ? 'market-resolved' : ''}`} onClick={onClick}>
      <div className={`market-list-item-image ${isDefaultImage ? 'default-image' : ''}`}>
        <img 
          src={marketImage} 
          alt="" 
          onError={(e) => {
            // Fallback to default if image fails to load
            (e.target as HTMLImageElement).src = DEFAULT_MARKET_IMAGE;
            (e.target as HTMLImageElement).parentElement?.classList.add('default-image');
          }}
        />
      </div>
      
      <div className="market-list-item-content">
        {/* Stats row at top */}
        <div className="market-list-item-stats">
          <div className="market-list-item-volume">
            <img 
              src="/assets/icons/solanaLogoMark.svg" 
              alt="SOL" 
              className="volume-sol-icon"
            />
            <span className="volume-amount">{poolSize}</span>
          </div>
          <span className="market-list-item-separator">·</span>
          <span className="market-list-item-time">
            {isResolved ? getEndedAgo() : (timeDisplay === 'Ended' ? 'Ended' : `${timeDisplay.replace(' left', '')}`)}
          </span>
        </div>
        
        {/* Question */}
        <div className="market-list-item-question">
          {displayQuestion(market.question)}
        </div>
        
        {/* Options list - simple text format (show for non-resolved) */}
        {!isResolved && (
          <div className="market-list-item-options">
            {allOptions.map((option, idx) => (
              <div key={idx} className="market-list-item-option">
                <span className="option-label">{displayOptionTitleCase(option.label)}</span>
                <span className="option-percentage">{option.percentage.toFixed(0)}%</span>
              </div>
            ))}
          </div>
        )}
        
        {/* For resolved markets, show options with winner highlighted */}
        {isResolved && (
          <div className="market-list-item-options">
            {allOptions.map((option, idx) => (
              <div key={idx} className={`market-list-item-option ${option.isWinner ? 'option-winner' : 'option-loser'}`}>
                <span className="option-label">
                  {option.isWinner && <span className="winner-dot">●</span>}
                  {displayOptionTitleCase(option.label)}
                </span>
                <span className="option-percentage">{option.percentage.toFixed(0)}%</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
